import { stitchSchemas } from '@graphql-tools/stitch';
import { introspectSchema, wrapSchema } from '@graphql-tools/wrap';
import { type AsyncExecutor } from '@graphql-tools/utils';
import {
  HttpLink,
  InMemoryCache,
  ApolloClient,
  ApolloLink,
  type NormalizedCacheObject,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { SchemaLink } from '@apollo/client/link/schema';
import { type GraphQLSchema, print } from 'graphql';
import Cookies from 'js-cookie';
import type {ErrorHandler} from 'types';
import { HIBOU_GRAPHQL_URL, TEST_ENV } from 'constants/env';
import initMockedData from './mock/initMockedData';
import { schemaExtensionResolvers } from './mock/schema/resolvers';
import mockSchema from './mock/schema/schema';
import { typeExtensions } from './mock/schema/typeDefs';

const referrerPolicy = 'no-referrer-when-downgrade';

const getInMemoryCache = () => new InMemoryCache({
  typePolicies: {
    Document: {
      keyFields: ['key'],
      fields: {
        sharedWith: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    Folder: {
      keyFields: ['key'],
    },
  },
});

const errorLink = (handleError : ErrorHandler): ApolloLink => onError(({ graphQLErrors, networkError }) => {
  
  const unauthorizedGraphQLError = graphQLErrors && graphQLErrors?.find(({ extensions }) => extensions?.code === 'Unauthorized');
  
  if (unauthorizedGraphQLError) {

    console.info('graphQLErrors catched in errorLink', graphQLErrors);
    handleError(graphQLErrors.toString());

  } else if (networkError && 'statusCode' in networkError && networkError?.statusCode !== 401) {
      console.info('networkError catched in errorLink', networkError);
      handleError(networkError.toString());
  }
});

const csrfAuthLink : ApolloLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers } : { headers : Record<string, string> }) => ({
    headers: withCSRFHeader(headers),
  }));
  return forward(operation);
});

const httpLink : ApolloLink = new HttpLink({
  credentials: 'include',
  uri: HIBOU_GRAPHQL_URL,
  fetchOptions: {
    referrerPolicy,
  },
});

const defaultHeaders : Record<string, string> = {
  'Content-Type': 'application/json',
};

const withCSRFHeader = (headers : Record<string, string>) : Record<string, string> => {
  const pac4jCsrfToken = Cookies.get('pac4jCsrfToken');
  return {
    ...headers,
    ...(pac4jCsrfToken && { pac4jCsrfToken }),
  };
};

const headersWithCSRFHeader = withCSRFHeader(defaultHeaders);

const createIntrospectionExecutor : AsyncExecutor = async ({ document, variables }) => {
  const query = print(document);

  const response = await fetch(HIBOU_GRAPHQL_URL, {
    method: 'POST',
    headers: headersWithCSRFHeader,
    credentials: 'include',
    body: JSON.stringify({ query, variables }),
    referrerPolicy,
  });

  return response.json();
};

const getClient = (mergedSchema : GraphQLSchema, handleError : ErrorHandler) : ApolloClient<NormalizedCacheObject> => {
  const schemaLink = new SchemaLink({ schema: mergedSchema });

  return new ApolloClient({
    link: errorLink(handleError).concat(schemaLink),
    cache: getInMemoryCache(),
  });
};

const mergeMockedSchema = async (handleError : ErrorHandler) : Promise<ApolloClient<NormalizedCacheObject>> => {

  const introspectionExecutor = createIntrospectionExecutor/* (handleError) */;

  const schema = await introspectSchema(introspectionExecutor);
  
  const hibouSchema = wrapSchema({ schema, executor: introspectionExecutor });

  const mergedSchema = stitchSchemas({
      resolvers: schemaExtensionResolvers,
      subschemas: [hibouSchema, mockSchema],
      typeDefs: typeExtensions,
    });

  return getClient(mergedSchema, handleError);
};

export const initGraphql = (handleError : ErrorHandler) : Promise<ApolloClient<NormalizedCacheObject>> => {
  
  initMockedData();
  // const apolloLink = getLink(handleError);

  if (TEST_ENV) {
    return mergeMockedSchema(handleError);
  } else {
    const apolloClient = new ApolloClient({
      link: from([csrfAuthLink, errorLink(handleError), httpLink]),
      cache: getInMemoryCache(),
    });

    return Promise.resolve(apolloClient);
  }
};